type GlobalSearchTarget = {
  kind: 'registry' | 'invoice' | 'voucher' | 'inbox' | 'project' | 'route'
  route: string
  recordId?: string
  registryKind?: 'customer' | 'supplier' | 'product'
  year?: number
  query?: string
  projectId?: string
  activityId?: string
  entryId?: string
}

type GlobalSearchItem = {
  id: string
  group: string
  groupLabel: string
  icon: string
  title: string
  subtitle: string
  tag: string
  meta?: string
  amount?: number
  searchValues: unknown[]
  target: GlobalSearchTarget
}

type GlobalSearchResult = Omit<GlobalSearchItem, 'searchValues'> & { score: number }
type GlobalSearchGroup = { key: string; label: string; total: number; items: GlobalSearchResult[] }

;(function registerGlobalSearch(scope: typeof globalThis) {
  const host = scope as any
  const list = (value: unknown): any[] => Array.isArray(value) ? value : []
  const text = (value: unknown): string => String(value ?? '').trim()
  const yearOf = (value: unknown): number | undefined => {
    const match = text(value).match(/(?:^|\D)((?:19|20)\d{2})(?:\D|$)/)
    return match ? Number(match[1]) : undefined
  }
  const normalize = (value: unknown): string => text(value)
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLocaleLowerCase()
    .replace(/ø/g, 'o')
    .replace(/æ/g, 'ae')
    .replace(/ß/g, 'ss')
    .replace(/[^a-z0-9]+/g, ' ')
    .trim()
  const amountValues = (...values: unknown[]): string[] => values.flatMap((value) => {
    const amount = Number(value)
    if (!Number.isFinite(amount)) return []
    const fixed = amount.toFixed(2)
    return [String(amount), fixed, fixed.replace('.', ','), Math.round(amount).toString()]
  })
  // Display strings resolve through the global t() at index-build time (never at module scope), so
  // they follow the active locale. The Norwegian default also keeps the index usable when t() is absent.
  const tr = (key: string, norwegian: string): string => (typeof host.t === 'function' ? host.t(key, norwegian) : norwegian)
  // Translated word plus its Norwegian original: the original stays searchable so "faktura" still finds English-titled rows.
  const word = (key: string, norwegian: string): { text: string; alias: string } => ({ text: tr(key, norwegian), alias: norwegian })
  const linesText = (rows: unknown): string => list(rows)
    .flatMap((row) => [row?.description, row?.name, row?.productName, row?.accountCode, row?.salesAccountCode, row?.costAccountCode])
    .filter(Boolean)
    .join(' ')
  const clean = (values: unknown[]): unknown[] => values.filter((value) => value !== undefined && value !== null && value !== '')
  const push = (items: GlobalSearchItem[], item: GlobalSearchItem) => {
    if (!item.id || !item.title) return
    items.push({ ...item, searchValues: clean([item.title, item.subtitle, item.meta, ...item.searchValues]) })
  }

  const buildIndex = (state: any = {}): GlobalSearchItem[] => {
    const items: GlobalSearchItem[] = []

    list(state.customers).forEach((row) => push(items, {
      id: `customer:${row.id}`, group: 'customers', groupLabel: 'Kunder', icon: 'users', tag: 'Kunde',
      title: text(row.name) || tr('srch.tag.customer', 'Kunde'), subtitle: clean([row.customerNumber, row.organizationNumber, row.email]).join(' · '),
      searchValues: [text(row.name) ? undefined : 'Kunde', row.id, row.customerNumber, row.memberNumber, row.organizationNumber, row.email, row.phone, row.address, row.postalCode, row.postalPlace, row.note,
        ...list(row.contactPersons).flatMap((contact) => [contact.name, contact.role, contact.email, contact.phone])],
      target: { kind: 'registry', route: 'kunder', registryKind: 'customer', recordId: text(row.id) },
    }))

    list(state.suppliers).forEach((row) => push(items, {
      id: `supplier:${row.id}`, group: 'suppliers', groupLabel: 'Leverandører', icon: 'building', tag: 'Leverandør',
      title: text(row.name) || tr('srch.tag.supplier', 'Leverandør'), subtitle: clean([row.supplierNumber, row.organizationNumber, row.email]).join(' · '),
      searchValues: [text(row.name) ? undefined : 'Leverandør', row.id, row.supplierNumber, row.organizationNumber, row.email, row.phone, row.address, row.postalCode, row.postalPlace],
      target: { kind: 'registry', route: 'leverandorer', registryKind: 'supplier', recordId: text(row.id) },
    }))

    list(state.products).forEach((row) => push(items, {
      id: `product:${row.id}`, group: 'products', groupLabel: 'Produkter', icon: 'box', tag: 'Produkt',
      title: text(row.name) || tr('srch.tag.product', 'Produkt'), subtitle: clean([row.itemNumber, row.sku, row.description]).join(' · '),
      meta: Number.isFinite(Number(row.unitPrice)) ? text(row.unitPrice) : undefined,
      amount: Number.isFinite(Number(row.unitPrice)) ? Number(row.unitPrice) : undefined,
      searchValues: [text(row.name) ? undefined : 'Produkt', row.id, row.itemNumber, row.sku, row.ean, row.gtin, row.description, row.productGroup, row.category, row.brand, row.variantName, row.supplierItemNumber, ...amountValues(row.unitPrice)],
      target: { kind: 'registry', route: 'produkter', registryKind: 'product', recordId: text(row.id) },
    }))

    list(state.invoices).forEach((row) => {
      const number = text(row.invoiceNumber || row.number || row.documentNumber || row.id)
      const customer = text(row.customer?.name || row.customerName)
      const kindWord = row.kind === 'credit' ? word('sup.status.credit', 'Kreditnota')
        : row.kind === 'offer' ? word('srch.kind.offer', 'Tilbud')
        : row.kind === 'order_confirmation' ? word('srch.kind.orderConfirmation', 'Ordrebekreftelse')
        : row.status === 'draft' ? word('srch.kind.invoiceDraft', 'Fakturautkast')
        : word('srch.kind.invoice', 'Faktura')
      const kind = kindWord.text
      const amount = Number(row.totalGross ?? row.amountGross ?? row.amount)
      push(items, {
        id: `invoice:${row.id}`, group: 'sales', groupLabel: 'Salg og fakturaer', icon: 'fileText', tag: kind,
        title: `${kind} ${number}`.trim(), subtitle: clean([customer, row.date || row.createdAt, row.status]).join(' · '),
        amount: Number.isFinite(amount) ? amount : undefined,
        searchValues: [kindWord.alias, row.id, number, customer, row.customer?.organizationNumber, row.customer?.email, row.customerReference, row.orderReference, row.recurringOccurrenceId, linesText(row.lines), ...amountValues(amount, row.totalNet, row.amountNet)],
        target: { kind: 'invoice', route: 'fakturaliste', recordId: text(row.id || number), year: yearOf(row.date || row.createdAt), query: number },
      })
    })

    list(state.expenses).filter((row) => row?.status !== 'draft').forEach((row) => {
      const supplier = text(row.supplierName || row.supplier?.name)
      const description = text(row.description || row.attachmentName || linesText(row.purchaseLines))
      const number = text(row.voucherNumber || row.invoiceNumber || row.supplierInvoiceNumber || row.id)
      const amount = Number(row.totalGross ?? row.amountGross ?? row.amount ?? row.amountNet)
      const labelWord = row.manualPosting ? word('nav.manualEntry', 'Fri postering')
        : row.isSupplierInvoice ? word('sup.ledger.type.supplierInvoice', 'Leverandørfaktura')
        : word('srch.tag.expense', 'Kjøp')
      const label = labelWord.text
      push(items, {
        id: `expense:${row.id}`, group: 'vouchers', groupLabel: 'Kjøp og bilag', icon: 'receipt', tag: label,
        title: supplier || description || `${label} ${number}`, subtitle: clean([number, description, row.date, row.status]).join(' · '),
        amount: Number.isFinite(amount) ? amount : undefined,
        searchValues: [supplier || description ? undefined : labelWord.alias, row.id, supplier, row.supplierId, row.supplierOrganizationNumber, number, row.kid, row.attachmentName, row.costAccountCode, row.settlementAccountCode, linesText(row.purchaseLines), linesText(row.manualPostingLines), ...amountValues(amount, row.amountNet)],
        target: { kind: 'voucher', route: 'transaksjon', recordId: text(row.id), year: yearOf(row.date), query: number || supplier || description },
      })
    })

    list(state.otherSales).filter((row) => !['draft', 'deleted'].includes(text(row?.status))).forEach((row) => {
      const description = text(row.description || linesText(row.lines))
      const number = text(row.voucherNumber || row.reference || row.id)
      const saleWord = word('srch.kind.sale', 'Salg')
      const amount = Number(row.totalGross ?? row.amountGross ?? row.amount ?? row.amountNet)
      push(items, {
        id: `other-sale:${row.id}`, group: 'sales', groupLabel: 'Salg og fakturaer', icon: 'fileText', tag: 'Annet salg',
        title: description || `${saleWord.text} ${number}`, subtitle: clean([number, row.customerName, row.date, row.status]).join(' · '),
        amount: Number.isFinite(amount) ? amount : undefined,
        searchValues: [description ? undefined : saleWord.alias, row.id, number, row.customerName, row.reference, row.settlementReference, linesText(row.lines), ...amountValues(amount, row.amountNet)],
        target: { kind: 'voucher', route: 'transaksjon', recordId: text(row.id), year: yearOf(row.date), query: number || description },
      })
    })

    list(state.receiptInbox).filter((row) => !['trashed', 'deleted'].includes(text(row?.status))).forEach((row) => {
      const supplier = text(row.receiptAnalysis?.supplierName)
      const fileName = text(row.attachmentName || row.name)
      const amount = Number(row.receiptAnalysis?.totalGross ?? row.amount)
      push(items, {
        id: `receipt:${row.id}`, group: 'documents', groupLabel: 'Dokumenter og innboks', icon: 'mail', tag: 'Innboks',
        title: supplier || fileName || tr('sup.voucherFallbackName', 'Bilag'), subtitle: clean([fileName, row.createdAt, row.status]).join(' · '),
        amount: Number.isFinite(amount) ? amount : undefined,
        searchValues: [supplier || fileName ? undefined : 'Bilag', row.id, supplier, fileName, row.receiptAnalysis?.invoiceNumber, row.receiptAnalysis?.organizationNumber, row.receiptAnalysis?.kid, row.receiptAnalysis?.description, ...amountValues(amount)],
        target: { kind: 'inbox', route: 'innboks', recordId: text(row.id), query: supplier || fileName },
      })
    })

    const projects = new Map(list(state.projects).map((row) => [text(row.id), row]))
    list(state.projects).forEach((row) => push(items, {
      id: `project:${row.id}`, group: 'projects', groupLabel: 'Prosjekter og timer', icon: 'clock', tag: 'Prosjekt',
      title: text(row.name) || tr('srch.tag.project', 'Prosjekt'), subtitle: clean([row.projectNumber, row.customerName, row.description, row.status]).join(' · '),
      searchValues: [text(row.name) ? undefined : 'Prosjekt', row.id, row.projectNumber, row.customerId, row.customerName, row.contactPerson, row.description, row.startDate, row.endDate],
      target: { kind: 'project', route: 'prosjekter', projectId: text(row.id), query: text(row.name) },
    }))
    list(state.projectActivities).forEach((row) => {
      const project = projects.get(text(row.projectId))
      push(items, {
        id: `activity:${row.id}`, group: 'projects', groupLabel: 'Prosjekter og timer', icon: 'clock', tag: 'Aktivitet',
        title: text(row.name) || tr('srch.tag.activity', 'Aktivitet'), subtitle: clean([project?.name, row.description, row.status]).join(' · '),
        searchValues: [text(row.name) ? undefined : 'Aktivitet', row.id, row.projectId, project?.projectNumber, project?.customerName, row.productId, row.billing, row.unitPrice, ...amountValues(row.unitPrice)],
        target: { kind: 'project', route: 'prosjekter', projectId: text(row.projectId), activityId: text(row.id), query: text(row.name) },
      })
    })

    list(state.mileageEntries).filter((row) => row?.status !== 'deleted').forEach((row) => push(items, {
      id: `mileage:${row.id}`, group: 'other', groupLabel: 'Annet i regnskapet', icon: 'car', tag: 'Kjørebok',
      title: clean([row.fromLocation, row.toLocation]).join(' → ') || text(row.purpose) || tr('srch.fallback.trip', 'Kjøretur'),
      subtitle: clean([row.purpose, row.date, row.kilometers && `${row.kilometers} km`]).join(' · '),
      searchValues: [clean([row.fromLocation, row.toLocation]).length || text(row.purpose) ? undefined : 'Kjøretur', row.id, row.stops, row.personName, row.vehicleRegistration, ...amountValues(row.kilometers, row.amountNet)],
      target: { kind: 'route', route: 'kjorebok', recordId: text(row.id), year: yearOf(row.date), query: text(row.purpose || row.fromLocation) },
    }))

    list(state.customerBillingGroups).filter((row) => !['archived', 'ended'].includes(text(row?.status))).forEach((row) => push(items, {
      id: `agreement:${row.id}`, group: 'other', groupLabel: 'Annet i regnskapet', icon: 'repeat', tag: row.type === 'list' ? tr('srch.kind.customerList', 'Kundeliste') : tr('srch.tag.agreement', 'Avtale'),
      title: text(row.name) || tr('srch.tag.agreement', 'Avtale'), subtitle: clean([row.description, row.interval, row.status]).join(' · '),
      searchValues: [text(row.name) ? undefined : 'Avtale', row.id, row.customerId, row.internalComment, row.nextInvoiceDate, ...amountValues(row.unitPrice)],
      target: { kind: 'route', route: 'abonnement', recordId: text(row.id), query: text(row.name) },
    }))

    list(state.settings?.companyDocuments).forEach((row) => push(items, {
      id: `company-document:${row.id || row.storagePath || row.name}`, group: 'documents', groupLabel: 'Dokumenter og innboks', icon: 'files', tag: 'Dokument',
      title: text(row.name || row.fileName) || tr('srch.tag.company-document', 'Dokument'), subtitle: clean([row.category, row.createdAt, row.description]).join(' · '),
      searchValues: [text(row.name || row.fileName) ? undefined : 'Dokument', row.id, row.storagePath, row.mimeType], target: { kind: 'route', route: 'innstillinger', recordId: text(row.id), query: text(row.name || row.fileName) },
    }))

    list(state.bankStatements).forEach((row) => push(items, {
      id: `bank-statement:${row.id}`, group: 'other', groupLabel: 'Annet i regnskapet', icon: 'bank', tag: 'Bankutskrift',
      title: text(row.fileName || row.name || row.account?.name) || tr('srch.tag.bank-statement', 'Bankutskrift'), subtitle: clean([row.periodFrom, row.periodTo, row.status]).join(' · '),
      searchValues: [text(row.fileName || row.name || row.account?.name) ? undefined : 'Bankutskrift', row.id, row.bankAccountId, row.account?.number, row.source], target: { kind: 'route', route: 'bank', recordId: text(row.id), year: yearOf(row.periodFrom), query: text(row.fileName || row.name) },
    }))

    return items
  }

  const scoreItem = (item: GlobalSearchItem, query: string): number => {
    const q = normalize(query)
    if (!q) return 0
    const tokens = q.split(' ').filter(Boolean)
    const title = normalize(item.title)
    const subtitle = normalize(item.subtitle)
    const haystack = item.searchValues.map(normalize).join(' ')
    if (!tokens.every((token) => title.includes(token) || subtitle.includes(token) || haystack.includes(token))) return 0
    let score = title === q ? 1000 : title.startsWith(q) ? 700 : title.includes(q) ? 500 : subtitle.includes(q) ? 300 : 150
    score += tokens.reduce((sum, token) => sum + (title.startsWith(token) ? 40 : title.includes(token) ? 20 : 0), 0)
    return score
  }

  const GROUP_ORDER = ['customers', 'suppliers', 'products', 'sales', 'vouchers', 'documents', 'projects', 'other']
  const search = (state: any, query: unknown, options: { limitPerGroup?: number } = {}): GlobalSearchGroup[] => {
    const limit = Math.max(1, Number(options.limitPerGroup) || 6)
    const matches = buildIndex(state).map((item) => ({ item, score: scoreItem(item, text(query)) })).filter((entry) => entry.score > 0)
    return GROUP_ORDER.map((group) => {
      const rows = matches.filter((entry) => entry.item.group === group).sort((a, b) => b.score - a.score || a.item.title.localeCompare(b.item.title))
      if (!rows.length) return null
      return {
        key: group, label: rows[0].item.groupLabel, total: rows.length,
        items: rows.slice(0, limit).map(({ item, score }) => {
          const result = { ...item }
          delete (result as Partial<GlobalSearchItem>).searchValues
          return { ...result, score }
        }),
      }
    }).filter(Boolean) as GlobalSearchGroup[]
  }

  const open = (item: GlobalSearchResult): void => {
    const target = item?.target
    if (!target?.route) return
    if (target.year) host.plummeSetYear?.(target.year)
    if (target.kind === 'registry' && target.registryKind && target.recordId) {
      host.PlummeRegistryLiveControl?.presentPageIntent?.({ action: 'open_record', kind: target.registryKind, recordId: target.recordId })
    } else if (target.kind === 'invoice') {
      host.__highlightInvoice = target.recordId || target.query || null
    } else if (target.kind === 'voucher') {
      const transactionTarget = { voucherId: target.recordId || '', year: target.year || null, businessId: host.PlummeBackend?.activeBusinessId || '', listContext: { search: target.query || '' } }
      host.__plummeTransactionTarget = transactionTarget
      host.__plummeTransactionRouteTarget = transactionTarget
    } else if (target.kind === 'inbox') {
      host.__plummeInboxSearchTarget = { id: target.recordId || '', query: target.query || '' }
    } else if (target.kind === 'project') {
      host.__plummeProjectSearchTarget = { projectId: target.projectId || '', activityId: target.activityId || '', entryId: target.entryId || '', query: target.query || '' }
    } else if (String(item.id || '').startsWith('mileage:')) {
      host.__plummeEditMileage = { id: target.recordId || '' }
    }
    host.plummeNav?.(target.route)
    host.setTimeout?.(() => {
      if (String(item.id || '').startsWith('company-document:')) host.dispatchEvent?.(new CustomEvent('plumme-settings-tab', { detail: 'dokumenter' }))
      host.dispatchEvent?.(new CustomEvent('plumme-global-search-open'))
    }, 0)
  }

  const installNavigation = (navigate: any, setYear: any) => {
    host.plummeNav = navigate
    host.plummeSetYear = setYear
    return () => { host.plummeNav = null; host.plummeSetYear = null }
  }
  const useTarget = (key: string, consume = false) => {
    const read = () => { const value = host[key] || null; if (consume) host[key] = null; return value }
    const [value, setValue] = host.React.useState(read)
    host.React.useEffect(() => {
      const update = () => { const next = read(); if (next) setValue(next) }
      host.addEventListener('plumme-global-search-open', update)
      return () => host.removeEventListener('plumme-global-search-open', update)
    }, [key, consume])
    return [value, setValue]
  }
  host.PlummeGlobalSearch = { buildIndex, normalize, open, search, installNavigation, useTarget }
})(typeof window !== 'undefined' ? window : globalThis)
